import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../utils/constants';
import api from '../../services/api';
import { pendingSsoResult, msalReady } from '../../config/msalInstance';

const SsoCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const { instance, inProgress } = useMsal();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [msalIsReady, setMsalIsReady] = useState(false);
  const hasProcessed = useRef(false);

  // Wait for MSAL to fully initialize and process any redirects
  useEffect(() => {
    msalReady.then(() => {
      setMsalIsReady(true);
    });
  }, []);

  const processLogin = useCallback(async (idToken: string) => {
    try {
      const { data } = await api.post('/auth/sso/login', { idToken });
      loginWithToken(data.token, data.user);

      if (data.user.role === 'admin') {
        navigate(ROUTES.ADMIN_DASHBOARD, { replace: true });
      } else {
        navigate(ROUTES.HOME, { replace: true });
      }
    } catch (err: any) {
      console.error('SSO backend error:', err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        'SSO authentication failed. Please try again.';
      setError(errorMessage);
    }
  }, [loginWithToken, navigate]);

  useEffect(() => {
    console.log('[SSO Callback] State:', {
      msalIsReady,
      inProgress,
      hasProcessed: hasProcessed.current,
      pendingSsoResult: pendingSsoResult ? 'present' : 'null',
      accountsCount: instance.getAllAccounts().length,
      currentUrl: window.location.href,
    });

    // Don't do anything until MSAL is fully ready
    if (!msalIsReady) {
      console.log('[SSO Callback] Waiting for MSAL to be ready...');
      return;
    }
    if (hasProcessed.current) {
      console.log('[SSO Callback] Already processed, skipping');
      return;
    }

    const handleSsoCallback = async () => {
      console.log('[SSO Callback] Processing callback...');

      // Check for pending result from App.tsx initialization
      if (pendingSsoResult?.idToken) {
        console.log('[SSO Callback] Found pendingSsoResult, processing login');
        hasProcessed.current = true;
        processLogin(pendingSsoResult.idToken);
        return;
      }

      // Check for existing accounts (MSAL should have populated these)
      const accounts = instance.getAllAccounts();
      console.log('[SSO Callback] Accounts:', accounts.length);
      if (accounts.length > 0) {
        console.log('[SSO Callback] Found account, acquiring token silently');
        hasProcessed.current = true;
        try {
          const response = await instance.acquireTokenSilent({
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0],
          });
          console.log('[SSO Callback] Got silent token, processing login');
          processLogin(response.idToken);
        } catch (err: any) {
          console.error('[SSO Callback] Silent token error:', err);
          setError('Failed to complete authentication.');
        }
        return;
      }

      // No pending result and no accounts - redirect to login
      console.log('[SSO Callback] No result and no accounts, redirecting to login');
      navigate(ROUTES.LOGIN, { replace: true });
    };

    // Wait for MSAL to finish any in-progress operations
    if (inProgress === InteractionStatus.None) {
      handleSsoCallback();
    } else {
      console.log('[SSO Callback] MSAL interaction in progress:', inProgress);
    }
  }, [instance, inProgress, msalIsReady, processLogin, navigate]);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ mb: 3, maxWidth: 400 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate(ROUTES.LOGIN, { replace: true })}>
          Return to Login
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <CircularProgress size={48} />
      <Typography sx={{ mt: 2 }}>Completing sign-in...</Typography>
    </Box>
  );
};

export default SsoCallbackPage;
